import type {
  AIInsightRequest,
  AIInsightCompletedJob,
  AIInsightCompletedListResponse,
  AIInsightRequestListResponse,
  BlogComment,
  BlogCommentsResponse,
  BlogListResponse,
  BlogMedia,
  BlogPost,
  BlogStructuredContent,
  BlogStructuredDirection,
  BlogStructuredIngredientSection,
  BlogStructuredStat,
  FdcBatchMacrosResponse,
  FdcFoodMacros,
  FdcFoodSearchItem,
  FdcFoodSearchResponse,
  PantryItem,
  PantryItemsResponse,
  PantryScanImage,
  PantryScanSubmission,
  VisitRecord,
  VisitSlot,
} from "../types";
import { resolvePublicAuthorName } from "./authorNames.ts";

type AnyObject = Record<string, unknown>;

function asObject(value: unknown): AnyObject | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as AnyObject;
}

function pickFirst<T>(obj: AnyObject, keys: string[], parse: (value: unknown) => T | null): T | null {
  for (const key of keys) {
    if (!(key in obj)) {
      continue;
    }
    const parsed = parse(obj[key]);
    if (parsed !== null) {
      return parsed;
    }
  }
  return null;
}

function toStringValue(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function toNumberValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toBooleanValue(value: unknown): boolean | null {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") {
      return true;
    }
    if (normalized === "false") {
      return false;
    }
  }
  return null;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.map(toStringValue).filter((entry): entry is string => Boolean(entry));
}

function hasLikelyImagePath(value: string): boolean {
  const lowered = value.toLowerCase();
  if (lowered.includes("pantry_scans/")) {
    return true;
  }
  return /\.(jpe?g|png|gif|webp|avif|bmp|svg)(\?|#|$)/i.test(value);
}

function resolveImageUrlCandidate(value: unknown): string | null {
  const raw = toStringValue(value);
  if (!raw) {
    return null;
  }

  const candidate = raw.trim();
  if (!candidate) {
    return null;
  }

  const lowered = candidate.toLowerCase();
  if (
    lowered.startsWith("http://") ||
    lowered.startsWith("https://") ||
    lowered.startsWith("data:image/") ||
    lowered.startsWith("blob:")
  ) {
    return candidate;
  }

  if (candidate.startsWith("//")) {
    return `https:${candidate}`;
  }

  if (candidate.startsWith("/")) {
    return candidate;
  }

  if (lowered.startsWith("media/") || lowered.startsWith("static/")) {
    return `/${candidate}`;
  }

  if (/^[a-z0-9.-]+\.s3[.-][a-z0-9-]+\.amazonaws\.com\//i.test(candidate)) {
    return `https://${candidate}`;
  }

  if (lowered.startsWith("pantry_scans/")) {
    return `/media/${candidate}`;
  }

  if (!hasLikelyImagePath(candidate)) {
    return null;
  }

  if (candidate.startsWith("./")) {
    return `/${candidate.slice(2)}`;
  }

  return `/${candidate.replace(/^\/+/, "")}`;
}

function toIsoDateString(value: unknown): string | null {
  const text = toStringValue(value);
  if (text) {
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
  }
  const number = toNumberValue(value);
  if (number === null) {
    return null;
  }
  const seconds = number > 10_000_000_000 ? number / 1000 : number;
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function extractItems(payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  const object = asObject(payload);
  if (!object) {
    return [];
  }
  const candidates = ["items", "results", "foods", "jobs", "data"];
  for (const key of candidates) {
    const value = object[key];
    if (Array.isArray(value)) {
      return value;
    }
  }
  return [];
}

function normalizeScanImageEntry(value: unknown): PantryScanImage | null {
  if (typeof value === "string") {
    const src = resolveImageUrlCandidate(value);
    if (!src) {
      return null;
    }
    return {
      id: src,
      url: src,
      presigned_url: src,
    };
  }

  const object = asObject(value);
  if (!object) {
    return null;
  }

  const nestedObjects = ["image", "asset", "file", "media", "photo", "source"]
    .map((key) => asObject(object[key]))
    .filter((entry): entry is AnyObject => Boolean(entry));
  const candidates = [object, ...nestedObjects];

  const resolveFromKeys = (keys: string[]): string | null => {
    for (const candidate of candidates) {
      const resolved = pickFirst(candidate, keys, resolveImageUrlCandidate);
      if (resolved) {
        return resolved;
      }
    }
    return null;
  };

  const url = resolveFromKeys([
    "url",
    "image_url",
    "imageUrl",
    "file_url",
    "source_url",
    "public_url",
    "publicUrl",
    "original_url",
    "download_url",
    "src",
    "path",
  ]);
  const presigned = resolveFromKeys([
    "presigned_url",
    "presignedUrl",
    "presignedURL",
    "presigned",
    "signed_url",
    "signedUrl",
    "preview_url",
    "thumbnail_url",
    "thumb_url",
    "thumbnail",
  ]);
  const sourceImage = resolveFromKeys(["source_image_key", "sourceImageKey"]);
  let s3Key: string | null = null;
  for (const candidate of candidates) {
    s3Key = pickFirst(candidate, ["s3_key", "s3Key", "key", "image_key", "imageKey"], toStringValue);
    if (s3Key) {
      break;
    }
  }
  const id = pickFirst(object, ["id", "image_id", "imageId", "media_id", "mediaId"], toStringValue) ?? s3Key ?? url ?? presigned ?? sourceImage;

  if (!id) {
    return null;
  }

  return {
    id,
    url: url ?? sourceImage ?? undefined,
    presigned_url: presigned ?? undefined,
  };
}

function normalizeScanImages(scan: AnyObject): PantryScanImage[] {
  const entries: PantryScanImage[] = [];
  const seen = new Set<string>();
  const keys = [
    "images",
    "image_urls",
    "photo_urls",
    "photos",
    "media",
    "files",
    "uploads",
    "submitted_images",
    "uploaded_images",
    "scan_images",
  ];

  for (const key of keys) {
    const value = scan[key];
    if (!Array.isArray(value)) {
      continue;
    }
    for (const entry of value) {
      const normalized = normalizeScanImageEntry(entry);
      if (!normalized || seen.has(normalized.id)) {
        continue;
      }
      seen.add(normalized.id);
      entries.push(normalized);
    }
  }

  const singleImage = pickFirst(
    scan,
    ["image_url", "photo_url", "url", "file_url", "preview_url", "thumbnail_url", "primary_image_url", "source_image_key"],
    resolveImageUrlCandidate,
  );
  if (singleImage && !seen.has(singleImage)) {
    entries.push({ id: singleImage, url: singleImage, presigned_url: singleImage });
  }

  return entries;
}

export function normalizeScan(value: unknown): PantryScanSubmission | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const id = pickFirst(object, ["id", "scan_id", "scanId", "submission_id", "submissionId"], toStringValue);
  if (!id) {
    return null;
  }

  const status = pickFirst(object, ["status", "processing_status"], toStringValue) ?? "unknown";
  const title = pickFirst(object, ["title", "name"], toStringValue);
  const notes = pickFirst(object, ["notes", "description"], toStringValue);
  const createdAt = pickFirst(object, ["created_at", "createdAt"], toIsoDateString);
  const updatedAt = pickFirst(object, ["updated_at", "updatedAt"], toIsoDateString);
  const processedAt = pickFirst(object, ["processed_at", "processedAt"], toIsoDateString);

  return {
    id,
    status,
    title: title ?? null,
    notes: notes ?? null,
    created_at: createdAt ?? undefined,
    updated_at: updatedAt ?? undefined,
    processed_at: processedAt ?? undefined,
    images: normalizeScanImages(object),
  };
}

export function normalizeScanList(payload: unknown): PantryScanSubmission[] {
  return extractItems(payload)
    .map(normalizeScan)
    .filter((entry): entry is PantryScanSubmission => Boolean(entry));
}

export function normalizePantryItem(value: unknown): PantryItem | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const pantryItemId = pickFirst(object, ["pantry_item_id", "pantryItemId", "item_id", "id"], toStringValue);
  const pantryItemIds =
    [object.pantry_item_ids, object.pantryItemIds, object.item_ids]
      .map(toStringArray)
      .find((entries) => entries.length > 0) ?? [];
  const submissionId = pickFirst(object, ["submission_id", "submissionId"], toStringValue) ?? pantryItemId ?? "unknown";
  const itemName =
    pickFirst(object, ["item_name", "itemName", "name"], toStringValue) ??
    pickFirst(object, ["normalized_name", "normalizedName"], toStringValue) ??
    "Unnamed item";
  const normalizedName = pickFirst(object, ["normalized_name", "normalizedName"], toStringValue) ?? itemName;
  const category = pickFirst(object, ["category"], toStringValue);
  const brand = pickFirst(object, ["brand", "brand_name", "brandName"], toStringValue);
  const fdcSource = pickFirst(object, ["fdc_source", "fdcSource", "source"], toStringValue);
  const fdcId = pickFirst(object, ["fdc_id", "fdcId"], toNumberValue);
  const quantityRaw = pickFirst(object, ["quantity"], (entry) => {
    const number = toNumberValue(entry);
    if (number !== null) {
      return String(number);
    }
    return toStringValue(entry);
  });
  const notes = pickFirst(object, ["notes"], toStringValue);
  const createdAt = pickFirst(object, ["created_at", "createdAt"], toIsoDateString) ?? new Date().toISOString();
  const sourceImageKey = pickFirst(object, ["source_image_key", "sourceImageKey"], toStringValue);

  return {
    pantry_item_id: pantryItemId ?? undefined,
    pantry_item_ids: pantryItemIds.length > 0 ? pantryItemIds : undefined,
    submission_id: submissionId,
    item_name: itemName,
    normalized_name: normalizedName,
    category: category ?? null,
    brand: brand ?? null,
    fdc_source: fdcSource ?? null,
    fdc_id: fdcId ?? null,
    quantity: quantityRaw ?? null,
    notes: notes ?? null,
    source_image_key: sourceImageKey ?? null,
    created_at: createdAt,
  };
}

export function normalizePantryItemsResponse(payload: unknown): PantryItemsResponse {
  const object = asObject(payload);
  const items = extractItems(payload)
    .map(normalizePantryItem)
    .filter((entry): entry is PantryItem => Boolean(entry));
  const nextCursor = object ? pickFirst(object, ["next_cursor", "nextCursor", "cursor"], toStringValue) : null;

  return {
    items,
    next_cursor: nextCursor ?? null,
  };
}

function extractSummaryText(value: unknown): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }
  const object = asObject(value);
  if (!object) {
    return null;
  }
  return pickFirst(
    object,
    ["short_summary", "shortSummary", "summary", "overall_assessment", "overallAssessment", "analysis"],
    toStringValue,
  );
}

export function normalizeAiInsight(value: unknown): AIInsightRequest | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const id = pickFirst(object, ["id", "job_id", "jobId"], toStringValue);
  if (!id) {
    return null;
  }

  const status = pickFirst(object, ["status"], toStringValue) ?? "unknown";
  const createdAt = pickFirst(object, ["created_at", "createdAt"], toIsoDateString);
  const updatedAt = pickFirst(object, ["updated_at", "updatedAt"], toIsoDateString);
  const summary = extractSummaryText(object.summary);
  const errorMessage =
    pickFirst(object, ["error_message", "errorMessage", "detail", "message"], toStringValue) ??
    pickFirst(asObject(object.error) ?? {}, ["message", "detail"], toStringValue);

  return {
    id,
    job_id: pickFirst(object, ["job_id", "jobId"], toStringValue) ?? id,
    status,
    created_at: createdAt ?? undefined,
    updated_at: updatedAt ?? undefined,
    summary: summary ?? null,
    error_message: errorMessage ?? null,
  };
}

export function normalizeAiInsightList(payload: unknown): AIInsightRequestListResponse {
  const items = extractItems(payload)
    .map(normalizeAiInsight)
    .filter((entry): entry is AIInsightRequest => Boolean(entry));
  return { items };
}

function normalizeBlogMedia(value: unknown): BlogMedia | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const url = pickFirst(object, ["url", "public_url", "publicUrl", "source_url", "src"], toStringValue);
  const type = pickFirst(object, ["type", "media_type", "mediaType"], toStringValue);
  const key = pickFirst(object, ["key", "s3_key", "s3Key"], toStringValue);

  if (!url && !key) {
    return null;
  }

  return {
    type: type ?? null,
    url: url ?? null,
    key: key ?? null,
    caption: pickFirst(object, ["caption"], toStringValue),
    alt_text: pickFirst(object, ["alt_text", "altText"], toStringValue),
  };
}

export function normalizeBlogMediaList(payload: unknown): BlogMedia[] {
  const object = asObject(payload);
  const source = object && Array.isArray(object.items) ? object.items : Array.isArray(payload) ? payload : [];
  return source.map(normalizeBlogMedia).filter((entry): entry is BlogMedia => Boolean(entry));
}

function normalizeBlogTags(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map(toStringValue).filter((entry): entry is string => Boolean(entry));
  }
  const asText = toStringValue(value);
  if (!asText) {
    return [];
  }
  return asText
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function pickMediaByType(media: BlogMedia[], expectedType: string): BlogMedia | null {
  const normalizedExpected = expectedType.trim().toUpperCase();
  const match = media.find((entry) => (entry.type || "").trim().toUpperCase() === normalizedExpected);
  return match ?? null;
}

function normalizeStructuredStat(value: unknown, index: number): BlogStructuredStat | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const id = pickFirst(object, ["id", "stat_id", "statId"], toStringValue) ?? `stat-${index}`;
  const label = pickFirst(object, ["label"], toStringValue) ?? "";
  const valueLabel = pickFirst(object, ["value"], toStringValue) ?? "";
  const secondaryValue = pickFirst(object, ["secondary_value", "secondaryValue"], toStringValue);

  if (!label && !valueLabel && !secondaryValue) {
    return null;
  }

  return {
    id,
    label,
    value: valueLabel,
    secondaryValue,
  };
}

function normalizeStructuredIngredientSection(value: unknown, index: number): BlogStructuredIngredientSection | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const items = toStringArray(object.items);
  const title = pickFirst(object, ["title"], toStringValue);
  if (items.length === 0 && !title) {
    return null;
  }

  return {
    id: pickFirst(object, ["id", "section_id", "sectionId"], toStringValue) ?? `ingredients-${index}`,
    title,
    items,
  };
}

function normalizeStructuredDirection(value: unknown, index: number): BlogStructuredDirection | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const title = pickFirst(object, ["title"], toStringValue);
  const body = pickFirst(object, ["body"], toStringValue) ?? "";
  const detail = pickFirst(object, ["detail"], toStringValue);
  if (!title && !body && !detail) {
    return null;
  }

  return {
    id: pickFirst(object, ["id", "direction_id", "directionId", "step_id", "stepId"], toStringValue) ?? `direction-${index}`,
    title,
    body,
    detail,
  };
}

function normalizeStructuredContent(
  value: unknown,
  options?: { impliedDisplayStyle?: BlogStructuredContent["displayStyle"] | null },
): BlogStructuredContent | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const displayStyleCandidate = pickFirst(object, ["displayStyle", "display_style"], toStringValue);
  const normalizedDisplayStyle = (displayStyleCandidate || options?.impliedDisplayStyle || "ARTICLE").trim().toUpperCase();
  const displayStyle = normalizedDisplayStyle === "RECIPE" ? "RECIPE" : "ARTICLE";

  return {
    displayStyle,
    servingText: pickFirst(object, ["servingText", "serving_text"], toStringValue),
    summaryTitle: pickFirst(object, ["summaryTitle", "summary_title"], toStringValue),
    summaryBody: pickFirst(object, ["summaryBody", "summary_body"], toStringValue),
    stats: (Array.isArray(object.stats) ? object.stats : [])
      .map((entry, index) => normalizeStructuredStat(entry, index))
      .filter((entry): entry is BlogStructuredStat => Boolean(entry)),
    ingredients: (Array.isArray(object.ingredients) ? object.ingredients : [])
      .map((entry, index) => normalizeStructuredIngredientSection(entry, index))
      .filter((entry): entry is BlogStructuredIngredientSection => Boolean(entry)),
    directions: (Array.isArray(object.directions) ? object.directions : [])
      .map((entry, index) => normalizeStructuredDirection(entry, index))
      .filter((entry): entry is BlogStructuredDirection => Boolean(entry)),
    notes: toStringArray(object.notes),
  };
}

export function normalizeBlog(value: unknown): BlogPost | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }

  const id = pickFirst(object, ["id", "blog_id", "blogId", "post_id", "postId"], toStringValue);
  if (!id) {
    return null;
  }

  const title = pickFirst(object, ["title", "headline"], toStringValue) ?? "Untitled post";
  const summary = pickFirst(object, ["summary", "excerpt"], toStringValue);
  const body = pickFirst(object, ["body", "content"], toStringValue);
  const status = pickFirst(object, ["status"], toStringValue) ?? "PUBLISHED";
  const tags = normalizeBlogTags(object.tags);

  const media = (Array.isArray(object.media) ? object.media : [])
    .map(normalizeBlogMedia)
    .filter((entry): entry is BlogMedia => Boolean(entry));

  const heroMedia = normalizeBlogMedia(object.hero_media) ?? pickMediaByType(media, "IMAGE");
  const videoMedia = normalizeBlogMedia(object.video_media) ?? pickMediaByType(media, "VIDEO");
  const structuredContent =
    normalizeStructuredContent(object.structuredContent) ??
    normalizeStructuredContent(object.structured_content) ??
    normalizeStructuredContent(object.recipe, { impliedDisplayStyle: "RECIPE" }) ??
    normalizeStructuredContent(object.recipeCard, { impliedDisplayStyle: "RECIPE" });
  const authorDisplayName = pickFirst(object, ["author_display_name", "authorDisplayName"], toStringValue);
  const authorFirstName = pickFirst(object, ["author_first_name", "authorFirstName"], toStringValue);
  const authorLastInitial = pickFirst(object, ["author_last_initial", "authorLastInitial"], toStringValue);
  const legacyAuthorName = pickFirst(object, ["author_name", "authorName"], toStringValue);
  const authorName = resolvePublicAuthorName(
    {
      author_display_name: authorDisplayName,
      author_first_name: authorFirstName,
      author_last_initial: authorLastInitial,
      legacy_label: legacyAuthorName,
    },
    "Anonymous",
  );

  return {
    id,
    title,
    summary: summary ?? null,
    body: body ?? null,
    status,
    tags,
    created_at: pickFirst(object, ["created_at", "createdAt"], toIsoDateString) ?? undefined,
    updated_at: pickFirst(object, ["updated_at", "updatedAt"], toIsoDateString) ?? undefined,
    published_at: pickFirst(object, ["published_at", "publishedAt"], toIsoDateString),
    reading_time_minutes: pickFirst(object, ["reading_time_minutes", "readingTimeMinutes"], toNumberValue),
    comment_count: pickFirst(object, ["comment_count", "commentCount"], toNumberValue) ?? undefined,
    upvotes: pickFirst(object, ["upvotes", "likes"], toNumberValue) ?? undefined,
    downvotes: pickFirst(object, ["downvotes", "dislikes"], toNumberValue) ?? undefined,
    score: pickFirst(object, ["score"], toNumberValue) ?? undefined,
    viewer_vote: pickFirst(object, ["viewer_vote", "viewerVote"], toNumberValue) ?? undefined,
    author_name: authorName,
    author_display_name: authorDisplayName,
    author_first_name: authorFirstName,
    author_last_initial: authorLastInitial,
    author_dietitian_id: pickFirst(object, ["author_dietitian_id", "authorDietitianId"], toStringValue),
    hero_media: heroMedia,
    video_media: videoMedia,
    media,
    structuredContent,
  };
}

export function normalizeBlogList(payload: unknown): BlogListResponse {
  const object = asObject(payload);
  const items = extractItems(payload)
    .map(normalizeBlog)
    .filter((entry): entry is BlogPost => Boolean(entry));
  const nextOffset = object ? pickFirst(object, ["next_offset", "nextOffset"], toNumberValue) : null;
  return {
    items,
    next_offset: nextOffset ?? null,
  };
}

export function normalizeBlogComment(value: unknown): BlogComment | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }
  const id = pickFirst(object, ["id", "comment_id", "commentId"], toStringValue);
  const body = pickFirst(object, ["body", "comment"], toStringValue);
  if (!id || !body) {
    return null;
  }
  const authorDisplayName = pickFirst(object, ["author_display_name", "authorDisplayName"], toStringValue);
  const authorFirstName = pickFirst(object, ["author_first_name", "authorFirstName"], toStringValue);
  const authorLastInitial = pickFirst(object, ["author_last_initial", "authorLastInitial"], toStringValue);
  const legacyAuthorLabel = pickFirst(
    object,
    ["author_label", "authorLabel", "author_name", "authorName"],
    toStringValue,
  );
  const authorLabel = resolvePublicAuthorName(
    {
      author_display_name: authorDisplayName,
      author_first_name: authorFirstName,
      author_last_initial: authorLastInitial,
      legacy_label: legacyAuthorLabel,
    },
    "Anonymous",
  );
  const depthValue = pickFirst(object, ["depth"], toNumberValue);
  const normalizedDepth = depthValue === null ? 0 : Math.max(0, Math.trunc(depthValue));

  return {
    id,
    blog_id: pickFirst(object, ["blog_id", "blogId"], toStringValue),
    body,
    created_at: pickFirst(object, ["created_at", "createdAt"], toIsoDateString) ?? undefined,
    updated_at: pickFirst(object, ["updated_at", "updatedAt"], toIsoDateString),
    author_label: authorLabel,
    author_display_name: authorDisplayName,
    author_first_name: authorFirstName,
    author_last_initial: authorLastInitial,
    author_user_id: pickFirst(object, ["author_user_id", "authorUserId"], toStringValue),
    parent_comment_id: pickFirst(object, ["parent_comment_id", "parentCommentId"], toStringValue),
    thread_root_comment_id: pickFirst(
      object,
      ["thread_root_comment_id", "threadRootCommentId"],
      toStringValue,
    ),
    depth: normalizedDepth,
  };
}

export function normalizeBlogComments(payload: unknown): BlogCommentsResponse {
  const items = extractItems(payload)
    .map(normalizeBlogComment)
    .filter((entry): entry is BlogComment => Boolean(entry));
  return { items };
}

function normalizeVisitSlot(value: unknown): VisitSlot | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }
  const startAt = pickFirst(object, ["start_at", "startAt"], toIsoDateString);
  const endAt = pickFirst(object, ["end_at", "endAt"], toIsoDateString);
  if (!startAt || !endAt) {
    return null;
  }
  return {
    start_at: startAt,
    end_at: endAt,
  };
}

function normalizeVisitRecord(value: unknown): VisitRecord | null {
  const object = asObject(value);
  if (!object) {
    return null;
  }
  const id = pickFirst(object, ["visit_id", "visitId", "id"], toStringValue);
  if (!id) {
    return null;
  }
  const preferredSlotsValue = pickFirst(object, ["preferred_slots", "preferredSlots"], (entry) =>
    Array.isArray(entry) ? entry : null,
  );
  return {
    id,
    status: pickFirst(object, ["status", "visit_status", "visitStatus"], toStringValue) ?? "REQUESTED",
    type: pickFirst(object, ["type", "visit_type", "visitType"], toStringValue),
    dietitian_id: pickFirst(object, ["dietitian_id", "dietitianId"], toStringValue),
    user_id: pickFirst(object, ["user_id", "userId"], toStringValue),
    preferred_slots: preferredSlotsValue
      ? preferredSlotsValue
          .map(normalizeVisitSlot)
          .filter((entry): entry is VisitSlot => Boolean(entry))
      : null,
    requested_start_at: pickFirst(object, ["requested_start_at", "requestedStartAt"], toIsoDateString),
    requested_end_at: pickFirst(object, ["requested_end_at", "requestedEndAt"], toIsoDateString),
    confirmed_start_at: pickFirst(object, ["confirmed_start_at", "confirmedStartAt"], toIsoDateString),
    confirmed_end_at: pickFirst(object, ["confirmed_end_at", "confirmedEndAt"], toIsoDateString),
    accepted_slot_index: pickFirst(object, ["accepted_slot_index", "acceptedSlotIndex", "accepted_slot"], toNumberValue),
    accepted_by_dietitian_id: pickFirst(
      object,
      ["accepted_by_dietitian_id", "acceptedByDietitianId", "accepted_by", "accepted_dietitian_id"],
      toStringValue,
    ),
    timezone: pickFirst(object, ["timezone"], toStringValue),
    location: pickFirst(object, ["location"], toStringValue),
    notes: pickFirst(object, ["notes"], toStringValue),
    meeting_url: pickFirst(object, ["meeting_url", "meetingUrl", "zoom_link", "zoomLink"], toStringValue),
    meeting_metadata: pickFirst(object, ["meeting_metadata", "meetingMetadata"], toStringValue),
    family_member_id: pickFirst(object, ["family_member_id", "familyMemberId"], toStringValue),
    cancellation_reason: pickFirst(object, ["cancellation_reason", "cancellationReason"], toStringValue),
    canceled_at: pickFirst(object, ["canceled_at", "canceledAt"], toIsoDateString),
    created_at: pickFirst(object, ["created_at", "createdAt"], toIsoDateString) ?? undefined,
    updated_at: pickFirst(object, ["updated_at", "updatedAt"], toIsoDateString) ?? undefined,
  };
}

export function normalizeVisitList(payload: unknown): VisitRecord[] {
  return extractItems(payload)
    .map(normalizeVisitRecord)
    .filter((entry): entry is VisitRecord => Boolean(entry));
}

export function normalizeCompletedAiInsightList(payload: unknown): AIInsightCompletedListResponse {
  const object = asObject(payload);
  const items = extractItems(payload)
    .map((entry): AIInsightCompletedJob | null => {
      const normalized = normalizeAiInsight(entry);
      if (!normalized) {
        return null;
      }
      const source = asObject(entry);
      return {
        ...normalized,
        id:
          (source ? pickFirst(source, ["job_id", "jobId", "id"], toStringValue) : normalized.id) ??
          normalized.id,
        job_id: source ? pickFirst(source, ["job_id", "jobId", "id"], toStringValue) ?? normalized.id : normalized.id,
        user_id: source ? pickFirst(source, ["user_id", "userId", "owner_id", "ownerId"], toStringValue) : null,
        user_name: source ? pickFirst(source, ["user_name", "userName"], toStringValue) : null,
        completed_at: source
          ? pickFirst(source, ["completed_at", "completedAt", "finished_at", "finishedAt"], toIsoDateString)
          : null,
      };
    })
    .filter((entry): entry is AIInsightCompletedJob => Boolean(entry));

  const cursor = object ? pickFirst(object, ["cursor"], toStringValue) : null;
  const nextCursor = object ? pickFirst(object, ["next_cursor", "nextCursor", "next"], toStringValue) : null;

  return {
    items,
    cursor: cursor ?? null,
    next_cursor: nextCursor ?? null,
  };
}

export function normalizeFoodSearchResponse(payload: unknown): FdcFoodSearchResponse {
  const object = asObject(payload);
  const items = extractItems(payload)
    .map((entry): FdcFoodSearchItem | null => {
      const food = asObject(entry);
      if (!food) {
        return null;
      }
      const source = pickFirst(food, ["source", "fdc_source", "fdcSource"], toStringValue) ?? "all";
      const fdcId = pickFirst(food, ["fdc_id", "fdcId"], toNumberValue);
      if (!fdcId || fdcId <= 0) {
        return null;
      }
      const description =
        pickFirst(food, ["description", "name", "item_name", "itemName"], toStringValue) ?? "Unknown food";
      const category = pickFirst(food, ["category", "food_category", "foodCategory"], toStringValue);
      const brandName = pickFirst(food, ["brand_name", "brandName", "brand_owner", "brandOwner"], toStringValue);
      const servingSize = pickFirst(food, ["serving_size", "servingSize"], toNumberValue);
      const servingUnit = pickFirst(food, ["serving_size_unit", "servingSizeUnit"], toStringValue);
      const id = pickFirst(food, ["id"], toStringValue) ?? `${source}-${fdcId}`;

      return {
        id,
        source,
        fdc_source: source,
        fdc_id: fdcId,
        description,
        category: category ?? null,
        brand_name: brandName ?? null,
        serving_size: servingSize ?? null,
        serving_size_unit: servingUnit ?? null,
      };
    })
    .filter((entry): entry is FdcFoodSearchItem => Boolean(entry));

  const total = object ? pickFirst(object, ["total", "count"], toNumberValue) : null;
  const hasMore = object ? pickFirst(object, ["has_more", "hasMore"], toBooleanValue) : null;

  return {
    items,
    total: total ?? undefined,
    has_more: hasMore ?? false,
  };
}

export function normalizeBatchMacrosResponse(payload: unknown): FdcBatchMacrosResponse {
  const items = extractItems(payload)
    .map((entry): FdcFoodMacros | null => {
      const macro = asObject(entry);
      if (!macro) {
        return null;
      }
      const source = pickFirst(macro, ["source", "fdc_source", "fdcSource"], toStringValue);
      const fdcId = pickFirst(macro, ["fdc_id", "fdcId"], toNumberValue);
      if (!source || !fdcId || fdcId <= 0) {
        return null;
      }
      return {
        source,
        fdc_id: fdcId,
        calories_kcal: pickFirst(macro, ["calories_kcal", "calories", "energy"], toNumberValue),
        protein_g: pickFirst(macro, ["protein_g", "protein"], toNumberValue),
        fat_g: pickFirst(macro, ["fat_g", "total_fat", "fat"], toNumberValue),
        carbs_g: pickFirst(macro, ["carbs_g", "carbs"], toNumberValue),
        fiber_g: pickFirst(macro, ["fiber_g", "fiber"], toNumberValue),
        sugar_g: pickFirst(macro, ["sugar_g", "sugars_g", "sugar"], toNumberValue),
        sodium_mg: pickFirst(macro, ["sodium_mg", "sodium"], toNumberValue),
      };
    })
    .filter((entry): entry is FdcFoodMacros => Boolean(entry));

  return { items };
}

export function normalizeAiInsightsRequestItems(payload: unknown): Array<Record<string, unknown>> {
  const items = Array.isArray(payload) ? payload : [];
  return items
    .map((entry): Record<string, unknown> | null => {
      const object = asObject(entry);
      if (!object) {
        return null;
      }
      const name = pickFirst(object, ["name", "item_name", "itemName"], toStringValue);
      if (!name) {
        return null;
      }

      const category = pickFirst(object, ["category"], toStringValue);
      const brand = pickFirst(object, ["brand", "brand_name", "brandName"], toStringValue);
      const notes = pickFirst(object, ["notes"], toStringValue);
      const normalizedName = pickFirst(object, ["normalized_name", "normalizedName"], toStringValue);
      const quantity = pickFirst(
        object,
        ["quantity"],
        (value) => toStringValue(value) ?? (toNumberValue(value) !== null ? String(toNumberValue(value)) : null),
      );

      return {
        name,
        category: category ?? null,
        brand: brand ?? null,
        notes: notes ?? null,
        normalized_name: normalizedName ?? null,
        quantity: quantity ?? null,
      };
    })
    .filter((entry): entry is Record<string, unknown> => entry !== null);
}
